import { Trans } from '@lingui/macro';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect, useState, useContext } from 'react';
import { Context } from '../..';
import DocumentsService from '../../services/DocumentsService';
import { ModalPopover } from './modal/modal-popover';
import { BallTriangle } from 'react-loader-spinner';
import { Link, useParams } from 'react-router-dom';
import { size } from 'underscore';
import { DocsEditModal } from './modal/DocsEditModal';
import { API_URL } from '../../appConfig';

/* ====== ДОП. ЛОГИКА ПЕРЕКЛАССИФИКАЦИИ ====== */
const REGIONAL_POINT_ID = 3;      // «Региональные»
const REPUBLICAN_POINT_ID = 5;    // «Республиканские методические разработки»

const MOVED_TITLE =
  'СБОРНИК МЕТОДИЧЕСКИХ РАЗРАБОТОК ПО ОБУЧЕНИЮ ДЕТЕЙ С РАССТРОЙСТВАМИ АУТИСТИЧЕСКОГО СПЕКТРА (РАС) ДОШКОЛЬНОГО И ШКОЛЬНОГО ВОЗРАСТА';

const MOVED_DATES = new Set(['2025-01-13', '2025-01-20', '2025-01-30']);

function isMovedDoc(d: any) {
  const title = String(d?.name ?? d?.title ?? '').toUpperCase();
  const date = String(d?.date ?? d?.publish_date ?? '').slice(0, 10);
  return title.includes(MOVED_TITLE) && MOVED_DATES.has(date);
}

/* ====== ДОП. ФЕДЕРАЛЬНЫЕ ДОКУМЕНТЫ (ВНЕШНИЕ ССЫЛКИ) ====== */
const FEDERAL_POINT_ID = 2;
const FEDERAL_EXTERN_LINKS = [
  {
    name:
      'Федеральный закон «Об образовании в Российской Федерации» от 29.12.2012 № 273-ФЗ',
    url: 'https://www.consultant.ru/document/cons_doc_LAW_140174/',
  },
  {
    name:
      'ФГОС НОО обучающихся с ОВЗ (приказ Минпросвещения России от 24.11.2022 № 1023)',
    url: 'https://fgos.ru/fgos/fgos-1598/',
  },
  {
    name:
      'ФГОС образования обучающихся с умственной отсталостью (приказ Минобрнауки России от 19.12.2014 № 1599)',
    url: 'https://fgos.ru/fgos/fgos-1599/',
  },
  {
    name:
      'Приказ Минпросвещения РФ от 24.11.2022 № 1022 — ФАООП дошкольного образования для обучающихся с ОВЗ',
    url: 'https://www.garant.ru/products/ipo/prime/doc/406149049/',
  },
  {
    name:
      'Приказ Минпросвещения РФ от 24.11.2022 № 1023 — ФАООП начального общего образования для обучающихся с ОВЗ',
    url: 'https://www.garant.ru/products/ipo/prime/doc/406486955/',
  },
  {
    name:
      'Приказ Минпросвещения РФ от 24.11.2022 № 1025 — ФАООП основного общего образования для обучающихся с ОВЗ',
    url: 'https://www.garant.ru/products/ipo/prime/doc/406486957/',
  },
  {
    name:
      'Приказ Минпросвещения РФ от 24.11.2022 № 1026 — ФАООП обучающихся с умственной отсталостью (интеллектуальными нарушениями)',
    url: 'https://www.garant.ru/products/ipo/prime/doc/405965157/',
  },
].map((x) => ({ ...x, external: true }));

/* ====== ЗАГРУЗКА С ПЕРЕРАСКЛАДКОЙ И ДОБАВЛЕНИЕМ ВНЕШНИХ ССЫЛОК ====== */
async function loadDocs(pointNum: number) {
  // Базовая загрузка для разделов без особой логики
  const baseLoad = async (p: number) => {
    const res = await DocumentsService.getDocsByType(1, p);
    return res.data.results ?? [];
  };

  // Для федерального раздела — добавляем внешние ссылки
  if (pointNum === FEDERAL_POINT_ID) {
    const items = await baseLoad(pointNum);
    return [...FEDERAL_EXTERN_LINKS, ...items];
  }

  // Для региональных/республиканских — перекладываем 3 сборника
  if (pointNum !== REGIONAL_POINT_ID && pointNum !== REPUBLICAN_POINT_ID) {
    return baseLoad(pointNum);
  }

  const [resRegional, resRepublic] = await Promise.all([
    DocumentsService.getDocsByType(1, REGIONAL_POINT_ID),
    DocumentsService.getDocsByType(1, REPUBLICAN_POINT_ID),
  ]);

  const regional = resRegional.data.results ?? [];
  const republican = resRepublic.data.results ?? [];

  if (pointNum === REGIONAL_POINT_ID) {
    return regional.filter((d: any) => !isMovedDoc(d));
  }
  // REPUBLICAN_POINT_ID
  const moved = regional.filter(isMovedDoc);
  return [...republican, ...moved];
}

/* ====== ПРЕЗЕНТАЦИОННЫЕ КОМПОНЕНТЫ ====== */
const Doc: FC<any> = observer(({ doc, setDelDocId, setDelDocModal }) => {
  const { store } = useContext(Context);
  const [docEditModal, setDocEditModal] = useState<boolean>(false);

  const href = doc?.external ? doc.url : API_URL + doc['url'];

  return (
    <div className="row m-3">
      <hr />
      <div className="col">
        <a
          href={href}
          className="doc-link"
          target="_blank"
          rel="noreferrer"
          // Для внешних ссылок скачивание не навязываем
          {...(!doc?.external ? { download: true } : {})}
        >
          <div className="h6">{doc['name']}</div>
          {!doc?.external && (
            <div>
              <span className="small me-1 text-primary">
                <Trans>Date</Trans>:
              </span>
              <span className="small me-1">{doc['date']}</span>
              <span className="small me-1 text-primary">
                <Trans>Chapter</Trans>:
              </span>
              <span className="small me-1">{doc['chapter']['chapter']}</span>
              <span className="small me-1 text-primary">
                <Trans>Publisher</Trans>:
              </span>
              <span className="small me-1">{doc['publisher']['publisher']}</span>
              <span className="small me-1 text-primary">
                <Trans>Status</Trans>:
              </span>
              <span className="small me-1">{doc['status']['status']}</span>
            </div>
          )}
        </a>
      </div>
      {store.user.is_superuser && !doc?.external ? (
        <div className="col-md-1">
          <i
            className="icon admin ms-3"
            onClick={() => {
              setDocEditModal(true);
            }}
          />
          <i
            className="icon trash"
            onClick={() => {
              setDelDocId(doc['id']);
              setDelDocModal(true);
            }}
          />
        </div>
      ) : null}
      {!doc?.external && (
        <DocsEditModal doc={doc} toggle={docEditModal} setToggle={setDocEditModal} />
      )}
    </div>
  );
});

const DocumentsByPoint: FC = () => {
  const { store } = useContext(Context);
  const { point } = useParams();
  const [documents, setDocuments] = useState<any[]>([]);
  const [delDocId, setDelDocId] = useState<number>(-1);
  const [delDocModal, setDelDocModal] = useState<boolean>(false);
  const [fix, setFix] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);

  const getDocs = async () => {
    const pointNum = Number(point);
    const items = await loadDocs(pointNum);
    setDocuments(items);
    setLoader(false);
  };

  useEffect(() => {
    setLoader(true);
    getDocs();
    setFix(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fix]);

  const deleteDoc = async () => {
    await DocumentsService.removeDocument(delDocId);
    setFix(true);
    setDelDocModal(false);
    setDelDocId(-1);
  };

  const getDocuments = () => {
    if (documents && size(documents)) {
      return documents
        .sort((a: any, b: any) => (b?.id ?? 0) - (a?.id ?? 0))
        .map((doc: any, i: number) => (
          <Doc key={i} {...{ doc, setDelDocId, setDelDocModal }} />
        ));
    }
    return <div className="pt-5 h6 text-center">{`Документы категории не найдены`}</div>;
  };

  return loader ? (
    <div className="row justify-content-center">
      <div className="col-md-2 mt-5">
        <BallTriangle color="#1d7ea7" height={90} width={90} />
      </div>
    </div>
  ) : (
    <div className="form-group">
      <div>
        <Link to={`/documents/`}>
          <button className="btn btn-light">
            <i className="icon arrow-left" />
            <Trans>Back</Trans>
          </button>
        </Link>
      </div>
      {getDocuments()}
      <ModalPopover
        toggle={delDocModal}
        setToggle={setDelDocModal}
        title={
          store.lang === 'ba'
            ? 'Файлды алып ташлау'
            : store.lang === 'ru'
            ? 'Удаление файла'
            : 'Deleting a file'
        }
        modalContent={<Trans>Do you want to delete a file?</Trans>}
        clickedButtonOne={() => deleteDoc()}
        clickedButtonTwo={() => {
          setDelDocModal(false);
          setDelDocId(-1);
        }}
        ButtonOneValue={store.lang === 'ba' ? 'Эйе' : store.lang === 'ru' ? 'Да' : 'Yes'}
        ButtonTwoValue={
          store.lang === 'ba' ? 'Кире ҡайтарыу' : store.lang === 'ru' ? 'Отмена' : 'Cancel'
        }
      />
    </div>
  );
};

export default observer(DocumentsByPoint);
